import React, { useRef } from 'react';
import T from 'prop-types';
import styled from 'styled-components';
import { variables } from '@splunk/themes';
import type { DataSourceEventPayload } from '@splunk/dashboard-types';
import EnterprisePopoverContentFactory from '../EnterprisePopoverContentFactory';
import PopoverStatusIcon from '../PopoverStatusIcon';
import { ProgressBarContainer } from './VisualizationProgressBar';
import { usePopoverState } from '../hooks/usePopoverState';
import { getIconStatus } from '../utils/getIconStatus';
import getStatusMessage from '../utils/getStatusMessage';

const statusIconHeight = 20;

const StatusIconContainer = styled.div.attrs(() => ({
    'data-test': 'status-icon-container',
}))`
    display: flex;
    align-items: center;
    position: absolute;
    top: 5px;
    right: -3px;
    z-index: 1;

    // Move the status icon when the progress bar pushes the title/desc/viz
    ${ProgressBarContainer}:not(:empty) ~ & {
        top: 8px;
    }

    &:focus-within svg {
        outline-style: solid;
        outline-offset: 1px;
        outline-width: 2px;
        outline-color: ${variables.focusColor};
    }
`;

const VizStatusIcon = ({
    dataSource,
    id,
}: {
    dataSource?: DataSourceEventPayload;
    id: string;
}): JSX.Element | null => {
    const anchor = useRef<HTMLElement | null>(null);
    const { open, onRequestOpen, onRequestClose } = usePopoverState({ anchor });

    const iconStatus = getIconStatus(dataSource);

    if (!iconStatus) {
        return null;
    }

    const statusMessage = getStatusMessage(dataSource);

    return (
        <StatusIconContainer>
            <PopoverStatusIcon
                height={statusIconHeight}
                iconSize={16}
                iconSizeOnHover={18}
                open={open}
                onRequestOpen={onRequestOpen}
                onRequestClose={onRequestClose}
                status={iconStatus}
                anchor={anchor}
                hasContent={!!statusMessage}
            >
                <EnterprisePopoverContentFactory
                    dataSource={dataSource}
                    componentId={id}
                    onRequestClose={onRequestClose}
                />
            </PopoverStatusIcon>
        </StatusIconContainer>
    );
};

VizStatusIcon.propTypes = {
    dataSource: T.object,
    id: T.string,
};

export default VizStatusIcon;
